/**
 * `truth-table`: the whole table, plus a chip per input so a reader can set a
 * case and watch its row light up.
 *
 * <div class="pc-widget" data-widget="truth-table" data-expression="a &amp;&amp; b"
 *      data-inputs='["a","b"]' data-rows='[[0,0,0],[0,1,0],[1,0,0],[1,1,1]]'
 *      data-caption="&amp;&amp; is true only when both sides are true.">
 *   <p class="pc-widget-fallback">a &amp;&amp; b is 1 only when a and b are both 1.</p>
 * </div>
 *
 * Every row is authored. Nothing here evaluates an expression: a widget that
 * computed the answer could disagree with the lesson.
 */

import { asText, button, buildShell, el, isStringOrNumber, parseJson, type WidgetText } from "./dom";

const isInputs = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string");

const isRows = (value: unknown): value is (string | number)[][] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((row) => Array.isArray(row) && row.every(isStringOrNumber));

export function renderTruthTable(host: HTMLElement, text: WidgetText): boolean {
  const inputs = parseJson(host.dataset.inputs, isInputs);
  const rows = parseJson(host.dataset.rows, isRows);
  if (!inputs || !rows) return false;
  const expression = host.dataset.expression ?? "result";

  const shell = buildShell(host, {
    label: text("labelTruthTable"),
    caption: host.dataset.caption,
  });

  const table = el("table");
  const thead = el("thead");
  const headRow = el("tr");
  for (const input of inputs) headRow.append(el("th", undefined, input));
  headRow.append(el("th", undefined, expression));
  thead.append(headRow);
  table.append(thead);

  const tbody = el("tbody");
  const bodyRows = rows.map((row) => {
    const tr = el("tr");
    for (const cell of row) tr.append(el("td", undefined, asText(cell)));
    tbody.append(tr);
    return tr;
  });
  table.append(tbody);
  shell.body.append(table);

  // One chip per input, toggling between the values that input takes in the
  // authored rows (so a three-valued column still works).
  const chosen = inputs.map((_, column) => asText(rows[0][column]));
  const chips = inputs.map((name, column) => {
    const values = [...new Set(rows.map((row) => asText(row[column])))];
    const chip = button(`${name} = ${chosen[column]}`, text("truthChange", { name }));
    chip.addEventListener("click", () => {
      const next = (values.indexOf(chosen[column]) + 1) % values.length;
      chosen[column] = values[next];
      chip.textContent = `${name} = ${chosen[column]}`;
      highlight();
    });
    return chip;
  });
  shell.controls.append(...chips);

  const highlight = () => {
    bodyRows.forEach((tr, index) => {
      const match = inputs.every((_, column) => asText(rows[index][column]) === chosen[column]);
      tr.classList.toggle("is-active", match);
    });
  };
  highlight();
  return true;
}
